import React, { useState } from "react";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useToast } from "../../hooks/use-toast";

const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast } = useToast();
    const [formErrors, setFormErrors] = useState({});

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setFormErrors({
            ...formErrors,
            [e.target.name]: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let errors = {};

        if (!formData.name) errors.name = 'Entre com um nome válido.';
        if (!formData.email) errors.email = 'Entre com um email válido.';
        if (!formData.message) errors.message = 'Diga como podemos te ajudar.';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        // Abrir cliente de email diretamente
        const { name, email, message } = formData;
        const subject = encodeURIComponent('Contato via site Manage4Me');
        const body = encodeURIComponent(`Nome: ${name}\nE-mail: ${email}\nMensagem: ${message}`);
        window.location.href = `mailto:manage4me.org@gmail.com?subject=${subject}&body=${body}`;

    };

    return (
        <section id="contato" className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                        Fale Conosco
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Entre em contato conosco. Estamos aqui para ajudar você a encontrar a melhor solução para seu negócio.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Contact Form */}
                    <Card className="shadow-xl border-0">
                        <CardHeader>
                            <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                                <Mail className="w-6 h-6 text-[#d03737] mr-3" />
                                Envie sua mensagem
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Nome *
                                    </label>
                                    <Input
                                        id="name"
                                        name="name"
                                        type="text"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full focus:ring-[#0c1248] focus:border-[#0c1248]"
                                        placeholder="Seu nome completo"
                                    />
                                    {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full focus:ring-[#0c1248] focus:border-[#0c1248]"
                                        placeholder="seu@email.com"
                                    />
                                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                        Mensagem *
                                    </label>
                                    <Textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        rows={5}
                                        className="w-full focus:ring-[#0c1248] focus:border-[#0c1248]"
                                        placeholder="Como podemos ajudar você?"
                                    />
                                    {formErrors.message && <span className="error-message">{formErrors.message}</span>}
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-gradient-to-r from-[#0c1248] to-[#d03737] hover:from-[#0c1248]/90 hover:to-[#d03737]/90 text-white py-3 group transition-all duration-300 transform hover:scale-105"
                                >
                                    {isSubmitting ? (
                                        "Enviando..."
                                    ) : (
                                        <>
                                            Enviar Mensagem
                                            <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    {/* Social Media Links */}
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                Siga-nos nas redes sociais
                            </h3>
                            <p className="text-gray-600 text-lg mb-8">
                                Acompanhe nossas novidades e dicas nas redes sociais.
                            </p>
                        </div>

                        <div className="flex space-x-6">
                            <a
                                href="#facebook"
                                className="group w-16 h-16 bg-gradient-to-r from-[#0c1248] to-[#d03737] rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg"
                            >
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                            </a>

                            <a
                                href="#instagram"
                                className="group w-16 h-16 bg-gradient-to-r from-[#d03737] to-[#0c1248] rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg"
                            >
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                            </a>

                            <a
                                href="#twitter"
                                className="group w-16 h-16 bg-gradient-to-r from-[#0c1248] to-[#d03737] rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg"
                            >
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;